import { useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useAppSelector } from '@app/hooks';

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.85; transform: scale(1); }
  50%      { opacity: 0;    transform: scale(1.8); }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${({ theme }) => theme.colors.primary}14 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.8s linear infinite;
    pointer-events: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  position: relative;
  z-index: 1;
`;

const DotWrap = styled.div`
  position: relative;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  z-index: 1;
`;

const Ripple = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  animation: ${pulse} 1.6s ease-out infinite;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
`;

const Counter = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const Stages = styled.div`
  display: flex;
  gap: 4px;
  position: relative;
  z-index: 1;
`;

const StageStep = styled.div<{ $state: 'done' | 'active' | 'pending' }>`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${({ $state, theme }) => {
    if ($state === 'done') return theme.colors.primary;
    if ($state === 'active') return theme.colors.primary;
    return theme.colors.border;
  }};
  opacity: ${({ $state }) => ($state === 'pending' ? 0.4 : 1)};
  transition: background ${({ theme }) => theme.motion.base} ease;
  position: relative;
  overflow: hidden;

  ${({ $state, theme }) =>
    $state === 'active' &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          ${theme.colors.primary} 0%,
          transparent 50%,
          ${theme.colors.primary} 100%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 1.2s linear infinite;
      }
    `}
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  position: relative;
  z-index: 1;
  line-height: 1.5;
`;

const HintRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const STAGE_ORDER = [
  'preparing',
  'loading_context',
  'started',
  'thinking',
  'window_done',
  'parsing',
  'validating',
  'finalizing',
] as const;
type StageName = (typeof STAGE_ORDER)[number];

const STAGE_LABELS: Record<StageName, string> = {
  preparing: 'Подготовка',
  loading_context: 'Подгрузка контекста и прецедентов',
  started: 'Контекст подготовлен',
  thinking: 'Модель анализирует договор',
  window_done: 'Обработка окон договора',
  parsing: 'Разбор ответа модели',
  validating: 'Проверка ссылок на нормы права',
  finalizing: 'Сохранение результатов',
};

/**
 * Циклические подсказки для long-running стадий (preparing, thinking).
 * Меняются каждые ~25 сек чтобы было видно «процесс идёт, мы не зависли».
 */
const PREPARING_HINTS = [
  'Подбираем шаблон договора из базы знаний для сравнения…',
  'Подгружаем применимые нормы права (ГК, отраслевые ФЗ)…',
  'Готовим контекст для модели — это занимает 30–60 секунд.',
];
const LOADING_CONTEXT_HINTS = [
  'Параллельно поднимаем шаблон, НПА и прецеденты из памяти агента…',
  'Гибридный отбор прецедентов: по статьям ГК, ключевым фразам и семантике.',
  'Это самые «свежие» знания агента — правки юриста и внешние аудиты.',
];
const THINKING_HINTS = [
  'DeepSeek в режиме thinking: chain-of-thought + анализ по 7 категориям рисков.',
  'Модель сверяет каждый пункт договора с нормами права и шаблоном.',
  'Длинные договоры режутся на окна и анализируются параллельно.',
  'Обычно на это уходит 2–6 минут. Можно закрыть карточку — результат сохранится.',
];

function formatElapsed(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}с`;
  return `${m}мин ${s.toString().padStart(2, '0')}с`;
}

interface Props {
  contractId: string;
  /** Сколько рисков было найдено в предыдущей версии договора (для контекста). */
  previousRisks?: number;
}

/**
 * Прогресс-блок в реальном времени для LLM-анализа договора.
 * Подключается к Redux-слайсу `analysisProgress`, который заполняется WS-bridge'ем
 * по событиям `contract_analysis_progress`. Показывает стадии, окна (для chunked),
 * накопительный счётчик рисков и elapsed-таймер.
 */
export function AnalysisProgressBar({ contractId, previousRisks = 0 }: Props) {
  const progress = useAppSelector((s) => s.analysisProgress.byContract[contractId]);
  const [now, setNow] = useState(Date.now());

  // Таймер тикает всегда пока компонент примонтирован — даже если progress
  // ещё не пришёл, у нас обычно есть stub из analysisStarted с started_at.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Fallback на случай прямого захода в карточку договора с status=under_review
  // когда stub не был создан (например, страница перезагружена после старта
  // анализа фоновым воркером). startedAt берём из useMemo чтоб не сбрасывался.
  const fallbackStartedAt = useMemo(() => Date.now(), [contractId]);

  const startedAt = progress?.started_at ?? fallbackStartedAt;
  const currentStage = (progress?.stage ?? 'preparing') as StageName | string;
  const stageIdx = STAGE_ORDER.indexOf(currentStage as StageName);
  const elapsed = now - startedAt;

  const title = STAGE_LABELS[currentStage as StageName] ?? 'Анализ договора';

  // Циклические подсказки: меняются каждые 25 секунд пока стадия не сменилась.
  // Даёт визуальный сигнал «процесс жив» в long-running preparing/thinking.
  const rotatingHint = (() => {
    const idx = Math.floor(elapsed / 25_000);
    if (currentStage === 'preparing') {
      return PREPARING_HINTS[idx % PREPARING_HINTS.length];
    }
    if (currentStage === 'loading_context') {
      return LOADING_CONTEXT_HINTS[idx % LOADING_CONTEXT_HINTS.length];
    }
    if (currentStage === 'thinking') {
      return THINKING_HINTS[idx % THINKING_HINTS.length];
    }
    return null;
  })();

  return (
    <Wrap>
      <Header>
        <DotWrap>
          <Ripple />
          <Dot />
        </DotWrap>
        <Title>{title}</Title>
        <Counter>прошло {formatElapsed(elapsed)}</Counter>
      </Header>

      <Stages>
        {STAGE_ORDER.map((s, idx) => (
          <StageStep
            key={s}
            $state={
              stageIdx < 0
                ? 'pending'
                : idx < stageIdx
                  ? 'done'
                  : idx === stageIdx
                    ? 'active'
                    : 'pending'
            }
            title={STAGE_LABELS[s]}
          />
        ))}
      </Stages>

      <HintRow>
        <Hint>
          {rotatingHint
            ? rotatingHint
            : currentStage === 'thinking' && progress?.window_total
              ? `Окон ${progress.window_current ?? 0} из ${progress.window_total} — обработка началась…`
              : currentStage === 'window_done' && progress?.window_total
                ? `Готово ${progress.window_current ?? 0} из ${progress.window_total} окон`
                : currentStage === 'parsing'
                  ? 'Парсим JSON-ответ модели, формируем структуру рисков.'
                  : currentStage === 'validating'
                    ? 'Проверяем найденные ссылки на статьи ГК / ФЗ через базу знаний — отмечаем неподтверждённые.'
                    : currentStage === 'finalizing'
                      ? 'Сохраняем риски и сроки в базу, отправляем уведомления.'
                      : 'Карточка обновится автоматически — можно закрыть и вернуться позже.'}
        </Hint>
        <Hint>
          Найдено рисков: <strong>{progress?.risks_so_far ?? 0}</strong>
          {previousRisks > 0 && currentStage !== 'finalizing' && (
            <> · предыдущая версия: {previousRisks}</>
          )}
        </Hint>
      </HintRow>
    </Wrap>
  );
}
